/**
 * Radar de Desempenho API Client
 *
 * API functions for the Radar de Desempenho feature.
 * All functions follow the established patterns from performance.ts
 */
import { get } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'
import type {
  RadarResult,
  RadarTreeResult,
  OrgOverviewResult,
  RadarComparisonResult,
  EvaluationMatrixResult,
  EvaluationMatrixDetailedResult,
} from '@/types/radar'

const API_NAME = 'performance'

// ============================================================================
// RADAR API
// ============================================================================

/**
 * Get complete radar for an employee
 *
 * Returns individual metrics, leader metrics (if applicable), and direct reports.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param employeeId - The employee ID to fetch radar for
 * @param period - Optional period filter
 * @returns Promise resolving to RadarResult
 */
export const getRadar = async (
  tenantDomain: string,
  employeeId: string,
  period?: { startDate?: string; endDate?: string }
): Promise<RadarResult> => {
  const queryParams = new URLSearchParams()
  if (period?.startDate) queryParams.append('periodStart', period.startDate)
  if (period?.endDate) queryParams.append('periodEnd', period.endDate)

  const query = queryParams.toString()
  const path = `/performance/radar/${employeeId}${query ? `?${query}` : ''}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as RadarResult
}

// ============================================================================
// ORG OVERVIEW API
// ============================================================================

/**
 * Get organization overview for executive landing
 *
 * Returns company-wide metrics, top performers, and areas needing attention.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param period - Optional period filter
 * @returns Promise resolving to OrgOverviewResult
 */
export const getOrgOverview = async (
  tenantDomain: string,
  period?: { startDate?: string; endDate?: string }
): Promise<OrgOverviewResult> => {
  const queryParams = new URLSearchParams()
  if (period?.startDate) queryParams.append('periodStart', period.startDate)
  if (period?.endDate) queryParams.append('periodEnd', period.endDate)

  const query = queryParams.toString()
  const path = `/performance/radar/org-overview${query ? `?${query}` : ''}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as OrgOverviewResult
}

// ============================================================================
// TREE API
// ============================================================================

/**
 * Get radar tree (hierarchical view)
 *
 * Returns org tree starting from the specified employee with configurable depth.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param employeeId - The root employee ID
 * @param depth - Tree depth (1-15, default 3)
 * @param period - Optional period filter
 * @returns Promise resolving to RadarTreeResult
 */
export const getRadarTree = async (
  tenantDomain: string,
  employeeId: string,
  depth = 3,
  period?: { startDate?: string; endDate?: string }
): Promise<RadarTreeResult> => {
  const queryParams = new URLSearchParams()
  queryParams.append('depth', Math.min(Math.max(depth, 1), 15).toString())
  if (period?.startDate) queryParams.append('periodStart', period.startDate)
  if (period?.endDate) queryParams.append('periodEnd', period.endDate)

  const path = `/performance/radar/${employeeId}/tree?${queryParams.toString()}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as RadarTreeResult
}

// ============================================================================
// COMPARISON API
// ============================================================================

/**
 * Compare two employees
 *
 * Returns side-by-side comparison of individual and leader metrics.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param employeeIdA - First employee ID
 * @param employeeIdB - Second employee ID
 * @param period - Optional period filter
 * @returns Promise resolving to RadarComparisonResult
 */
export const compareEmployees = async (
  tenantDomain: string,
  employeeIdA: string,
  employeeIdB: string,
  period?: { startDate?: string; endDate?: string }
): Promise<RadarComparisonResult> => {
  const queryParams = new URLSearchParams()
  queryParams.append('compare', employeeIdB)
  if (period?.startDate) queryParams.append('periodStart', period.startDate)
  if (period?.endDate) queryParams.append('periodEnd', period.endDate)

  const path = `/performance/radar/${employeeIdA}/compare?${queryParams.toString()}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as RadarComparisonResult
}

// ============================================================================
// EVALUATION MATRIX API
// ============================================================================

/**
 * Get evaluation matrix for an employee
 *
 * Returns pillar breakdown, radar chart data, and evaluation item details.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param employeeId - The employee ID
 * @param cycleId - Optional evaluation cycle ID (defaults to current)
 * @param detailed - Include individual evaluator scores (default false)
 * @returns Promise resolving to EvaluationMatrixResult or EvaluationMatrixDetailedResult
 */
export const getEvaluationMatrix = async (
  tenantDomain: string,
  employeeId: string,
  cycleId?: string,
  detailed = false
): Promise<EvaluationMatrixResult | EvaluationMatrixDetailedResult> => {
  const queryParams = new URLSearchParams()
  if (cycleId) queryParams.append('cycleId', cycleId)
  if (detailed) queryParams.append('detailed', 'true')

  const query = queryParams.toString()
  const path = `/performance/radar/${employeeId}/evaluation-matrix${query ? `?${query}` : ''}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()

  if (detailed) {
    return data as unknown as EvaluationMatrixDetailedResult
  }
  return data as unknown as EvaluationMatrixResult
}
